import * as THREE from 'three'
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js'
import { createMeshLoadResult } from '@/plugins/mesh/loaders/createMeshLoadResult'
import type {
  GltfAccessorReader,
  GltfBufferSource,
} from '@/plugins/mesh/loaders/gltfLowMemory/interfaces'
import type {
  GltfAccessorArray,
  GltfComponentType,
  GltfManifestDocument,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'
import { MeshPrimitiveNormalizer } from '@/plugins/mesh/MeshPrimitiveNormalizer'
import type { MeshLoadResult } from '@/plugins/mesh/types'
import type {
  GltfExtensionAccessorData,
  GltfExtensionPrimitive,
  GltfExtensionRegistry,
  GltfExtensionResult,
} from '@/runtime/GltfExtensionRegistry'

interface ThreeGltfParserBridge {
  readonly json: GltfManifestDocument['manifest']
  readonly associations?: Map<THREE.Object3D, { meshes?: number; primitives?: number }>
  getDependency(
    type: 'accessor',
    index: number
  ): Promise<THREE.BufferAttribute | THREE.InterleavedBufferAttribute>
}

function readThreeComponent(
  attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
  index: number,
  component: number
): number {
  switch (component) {
    case 0:
      return attribute.getX(index)
    case 1:
      return attribute.getY(index)
    case 2:
      return attribute.getZ(index)
    case 3:
      return attribute.getW(index)
    default:
      throw new Error(`不支持的 glTF accessor component: ${component}`)
  }
}

function normalizedComponent(value: number, componentType: GltfComponentType): number {
  switch (componentType) {
    case 5120:
      return Math.max(value / 127, -1)
    case 5121:
      return value / 255
    case 5122:
      return Math.max(value / 32767, -1)
    case 5123:
      return value / 65535
    case 5125:
      return value / 4294967295
    case 5126:
      return value
  }
}

function toFloat32(
  array: GltfAccessorArray,
  normalized: boolean,
  componentType: GltfComponentType
): Float32Array {
  if (!normalized && array instanceof Float32Array) return array.slice()
  const result = new Float32Array(array.length)
  for (let index = 0; index < array.length; index += 1) {
    result[index] = normalized ? normalizedComponent(array[index], componentType) : array[index]
  }
  return result
}

/** Three GLTFLoader 的 accessor/association 私有边界适配。 */
export async function processThreeGltfExtensions(
  registry: GltfExtensionRegistry,
  gltf: GLTF,
  signal?: AbortSignal
): Promise<ReadonlyMap<string, GltfExtensionResult>> {
  const parser = gltf.parser as unknown as ThreeGltfParserBridge
  const document = parser.json
  const handledObjects: THREE.Object3D[] = []
  for (const [object, association] of parser.associations ?? []) {
    if (!association) continue
    const meshIndex = association.meshes
    const primitiveIndex = association.primitives
    if (meshIndex === undefined || primitiveIndex === undefined) continue
    const primitive = document.meshes?.[meshIndex]?.primitives[primitiveIndex]
    if (primitive && registry.handlesPrimitive(primitive)) handledObjects.push(object)
  }
  const results = await registry.process({
    document,
    signal,
    accessorReader: {
      read: async (accessorIndex) => {
        const attribute = await parser.getDependency('accessor', accessorIndex)
        const data = new Float32Array(attribute.count * attribute.itemSize)
        for (let index = 0; index < attribute.count; index += 1) {
          for (let component = 0; component < attribute.itemSize; component += 1) {
            data[index * attribute.itemSize + component] = readThreeComponent(
              attribute,
              index,
              component
            )
          }
        }
        return { count: attribute.count, componentCount: attribute.itemSize, data }
      },
    },
  })

  // Three 会把未知 POINTS primitive 也建成 Object3D；已由 hook 接管的 primitive 不得再作为 Mesh 发布。
  for (const object of handledObjects) object.removeFromParent()
  return results
}

/** 低内存 accessor reader 到 extension hook 的 stride/normalized 保真适配。 */
export function createLowMemoryExtensionAccessorReader(
  reader: GltfAccessorReader,
  document: GltfManifestDocument,
  sources: readonly GltfBufferSource[]
): { read(accessorIndex: number, signal?: AbortSignal): Promise<GltfExtensionAccessorData> } {
  return {
    read: async (accessorIndex, signal) => {
      const result = await reader.read(document, accessorIndex, sources, signal)
      return {
        count: result.layout.count,
        componentCount: result.layout.componentCount,
        data: toFloat32(result.array, result.layout.normalized, result.layout.componentType),
      }
    },
  }
}

/** 从 Mesh 直接构建文档中剥离已由 extension hook 接管的 primitive。 */
export function filterHandledGltfPrimitives(
  registry: GltfExtensionRegistry,
  document: GltfManifestDocument
): GltfManifestDocument {
  const registeredNames = new Set(registry.extensionNames)
  const handledExtensionNames = new Set<string>()
  for (const mesh of document.manifest.meshes ?? []) {
    for (const primitive of mesh.primitives) {
      if (!registry.handlesPrimitive(primitive as GltfExtensionPrimitive)) continue
      for (const extension of Object.keys(primitive.extensions ?? {})) {
        if (registeredNames.has(extension)) handledExtensionNames.add(extension)
      }
    }
  }
  const meshes = document.manifest.meshes?.map((mesh) => ({
    ...mesh,
    primitives: mesh.primitives.filter(
      (primitive) => !registry.handlesPrimitive(primitive as GltfExtensionPrimitive)
    ),
  }))
  return {
    ...document,
    manifest: {
      ...document.manifest,
      meshes,
      extensionsRequired: document.manifest.extensionsRequired?.filter(
        (extension) => !handledExtensionNames.has(extension)
      ),
    },
  }
}

export function hasHandledGltfPrimitives(
  registry: GltfExtensionRegistry,
  document: GltfManifestDocument
): boolean {
  return (
    document.manifest.meshes?.some((mesh) =>
      mesh.primitives.some((primitive) =>
        registry.handlesPrimitive(primitive as GltfExtensionPrimitive)
      )
    ) ?? false
  )
}

export function hasExtensionResources(results: ReadonlyMap<string, GltfExtensionResult>): boolean {
  for (const result of results.values()) {
    if (result.resourcesByMesh.some((resources) => (resources?.length ?? 0) > 0)) return true
  }
  return false
}

export function hasHandledExtensionPrimitives(
  results: ReadonlyMap<string, GltfExtensionResult>
): boolean {
  for (const result of results.values()) {
    if (result.handledPrimitiveCount > 0) return true
  }
  return false
}

export function createEmptyMeshLoadResult(format: 'gltf' | 'glb'): MeshLoadResult {
  return createMeshLoadResult(
    format,
    new MeshPrimitiveNormalizer().normalize(new THREE.Group()),
    { ignoredAnimations: [], warnings: [] },
    0
  )
}

/** 组合 Mesh 与容器 extension 结果的幂等所有权。 */
export function attachGltfExtensionResults(
  result: MeshLoadResult,
  gltfExtensions: ReadonlyMap<string, GltfExtensionResult>
): MeshLoadResult {
  if (gltfExtensions.size === 0) return result
  const warnings = Array.from(gltfExtensions.values()).flatMap((extension) =>
    extension.diagnostics.map((diagnostic) => diagnostic.message)
  )
  const diagnostics = {
    ...result.diagnostics,
    warnings: [...result.diagnostics.warnings, ...warnings],
  }
  let disposed = false
  return {
    ...result,
    diagnostics,
    gltfExtensions,
    report: { ...result.report, diagnostics },
    dispose: () => {
      if (disposed) return
      disposed = true
      let firstError: unknown
      try {
        result.dispose()
      } catch (error) {
        firstError = error
      }
      for (const extension of Array.from(gltfExtensions.values()).reverse()) {
        try {
          extension.dispose()
        } catch (error) {
          firstError ??= error
        }
      }
      if (firstError) throw firstError
    },
  }
}

export function disposeGltfExtensionResults(
  results: ReadonlyMap<string, GltfExtensionResult> | undefined
): void {
  if (!results) return
  for (const result of Array.from(results.values()).reverse()) {
    try {
      result.dispose()
    } catch {
      // Loader failure path 保留原始错误。
    }
  }
}
